import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useAuth } from "src/hooks/use-auth";
import { useRouter } from "src/hooks/use-router";
import { useEstateAppApiContext } from "src/contexts/snug/estateAppApi-context";
import { paths } from "src/paths";
import { SplashScreen } from "src/components/splash-screen";
import { slackHook } from "src/utils/snug/slackHooks";
import type { UserDataObj, ProfessionalGroup } from "src/types/snugtotal";

export type GetStartedStep = "payment" | "onboarding";

interface GetStartedGuardProps {
  step: GetStartedStep;
  children: ReactNode;
}

export const GetStartedGuard: FC<GetStartedGuardProps> = ({ step, children }) => {
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const { estateAppApiInstance } = useEstateAppApiContext();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Unauthenticated — let them proceed through the signup flow
      setChecked(true);
      return;
    }

    const controller = new AbortController();

    const checkStatus = async () => {
      try {
        // Fetch current user's data
        const udRes = await estateAppApiInstance.get("/v3/user-data/", {
          params: { expand: "professional_group_role" },
          signal: controller.signal,
        });
        const userData: UserDataObj = udRes.data.data;

        if (!userData.professional_group_role_user_data) {
          slackHook(
            `🚨 NO PRO GROUP: Authenticated user has no professional group on get-started page!\n` +
              `ud_id: ${userData.ud_id}\n` +
              `email: ${userData.contact_email}`
          );
          router.replace(paths.getStarted.account);
          return;
        }

        // Fetch professional group to check onboarding status
        const proGroupId = userData.professional_group_role_user_data.professional_group_id;
        const pgRes = await estateAppApiInstance.get(
          `/v3/${userData.ud_id}/pro-group/${proGroupId}/`,
          { signal: controller.signal }
        );
        const proGroup: ProfessionalGroup = pgRes.data.data;

        const hasPayment = !!proGroup.onboarding_payment_date;
        const hasCompletion = !!proGroup.onboarding_completed_at;

        if (step === "payment") {
          if (hasPayment && hasCompletion) {
            router.replace(paths.index);
          } else if (hasPayment) {
            router.replace(paths.getStarted.onboarding);
          } else {
            setChecked(true); // No payment yet — correct page
          }
        } else {
          // step === "onboarding"
          if (hasPayment && hasCompletion) {
            router.replace(paths.index);
          } else if (!hasPayment) {
            router.replace(paths.getStarted.payment); // Skipped payment step
          } else {
            setChecked(true); // Has payment, not complete — correct page
          }
        }
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          console.error("GetStartedGuard: status check failed", err);
          setChecked(true); // On unexpected error, let them proceed
        }
      }
    };

    checkStatus();

    return () => controller.abort();
  }, [isAuthenticated, step, router, estateAppApiInstance]);

  if (!checked) {
    return <SplashScreen />;
  }

  return <>{children}</>;
};
